/**
 * Даты. По проводам ходит ISO (гггг-мм-дд), пользователь видит дд.мм.гггг.
 * В исходной таблице даты записаны как «18.11.1982г» — с буквой «г»
 * на конце, её тоже надо уметь съесть при импорте.
 */

import { APP_TIME_ZONE } from '../constants/time-zone';

const RU_DATE = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})/;

/** Показ пользователю: 1982-11-18 → 18.11.1982 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Разбор пользовательского ввода и данных импорта.
 * Понимает: 18.11.1982 · 18.11.82 · 18/11/1982 · 18-11-1982 · 18.11.1982г · 1982-11-18
 * Возвращает ISO или null.
 */
export function parseDate(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;

  // Уже ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return isRealDate(s) ? s : null;
  }

  const m = RU_DATE.exec(s);
  if (!m) return null;

  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = Number(m[3]);

  if (year < 100) {
    // Двузначный год: 82 → 1982, 05 → 2005. Порог — текущий год + 5.
    const currentTwo = new Date().getFullYear() % 100;
    year = year <= currentTwo + 5 ? 2000 + year : 1900 + year;
  }

  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return isRealDate(iso) ? iso : null;
}

/** Дата рождения: кроме обычных форматов принимает ДДММГГГГ без разделителей. */
export function parseBirthDate(input: string | null | undefined): string | null {
  const s = input?.trim();
  if (!s) return null;
  if (/^\d{8}$/.test(s)) {
    return parseDate(`${s.slice(0, 2)}.${s.slice(2, 4)}.${s.slice(4)}`);
  }
  return parseDate(s);
}

/** Убирает обозначение года, сохраняя цифры и формат, в том числе неполную дату. */
export function stripBirthDateYearSuffix(input: string): string {
  const match = /^(\d[\d.\s/-]*?)\s*г\.?$/iu.exec(input.trim());
  return match ? match[1]!.trimEnd() : input;
}

/**
 * Разбор того, что человек набрал в ячейке даты руками.
 *
 * Сокращения: «5» — пятое число текущего месяца, «5.11» — пятое ноября
 * текущего года. Всё остальное отдаётся `parseDate` без изменений: его
 * регулярка требует два разделителя и год, поэтому обе короткие формы
 * до сих пор просто отбрасывались. Значит, ни один ввод, который система
 * принимает сегодня, не меняет смысла — сокращения занимают пустое место.
 *
 * Вставке из буфера и импорту эта функция не достаётся, и это не упущение:
 * в книге голая цифра в столбце дат — чаще не дата, а серийный номер или
 * съехавшая колонка. Правдоподобное число месяца поверх настоящего значения
 * не найдёт потом даже фильтр «не заполнено».
 *
 * `today` передаётся снаружи, чтобы тест про «текущий месяц» не разъезжался
 * на границе месяца.
 */
export function parseDateEntry(
  input: string | null | undefined,
  today?: string,
): string | null {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;

  const base = today ?? todayIso();
  const year = base.slice(0, 4);
  const month = base.slice(5, 7);

  const dayOnly = /^(\d{1,2})$/.exec(s);
  if (dayOnly) return shortIso(year, month, dayOnly[1]!);

  const dayMonth = /^(\d{1,2})[.\-/](\d{1,2})$/.exec(s);
  if (dayMonth) return shortIso(year, dayMonth[2]!, dayMonth[1]!);

  return parseDate(s);
}

function shortIso(year: string, month: string, day: string): string | null {
  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  return isRealDate(iso) ? iso : null;
}

/** Отсекает 31.02 и подобное: Date его молча превратит в 3 марта. */
function isRealDate(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/**
 * День недели по-русски. Нужен вычисляемой колонке «День недели»
 * в листах «График» и «Занятость» — в книге она набита руками, и в ней
 * уже разъехались «вторник» и «вторник » с пробелом.
 */
const WEEKDAYS_RU = [
  'воскресенье',
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
] as const;

export function weekdayRu(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  // UTC-полдень, а не локальная полночь: иначе в часовых поясах западнее
  // Гринвича дата съезжает на сутки назад и день недели врёт
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(d.getTime()) ? '' : (WEEKDAYS_RU[d.getUTCDay()] ?? '');
}

/** Дата плюс дни. Считается в UTC — сутки везде ровно 86400 секунд. */
export function addDaysIso(iso: string | null | undefined, days: number): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Сколько суток от `from` до `to`. Отрицательное — `to` уже прошла. */
export function daysBetweenIso(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const parse = (s: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12) : null;
  };
  const a = parse(from);
  const b = parse(to);
  if (a === null || b === null) return null;
  return Math.round((b - a) / 86_400_000);
}

export function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const BUSINESS_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: APP_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
});

/** Календарный день в рабочей зоне, независимо от зоны браузера или процесса. */
export function businessTodayIso(nowDate: Date = new Date()): string {
  const parts = BUSINESS_DATE_FORMATTER.formatToParts(nowDate);
  return ['year', 'month', 'day']
    .map((type) => parts.find((part) => part.type === type)!.value)
    .join('-');
}

/** Возраст в полных годах на текущий рабочий день. */
export function ageFrom(iso: string | null | undefined, today = businessTodayIso()): number | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso) || !isRealDate(iso)
    || !/^\d{4}-\d{2}-\d{2}$/.test(today) || !isRealDate(today) || iso > today) return null;
  let age = Number(today.slice(0, 4)) - Number(iso.slice(0, 4));
  if (today.slice(5) < iso.slice(5)) age--;
  return age;
}

/** «6 августа» — как в разделителях исходной таблицы. */
const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

export function formatDateHuman(iso: string | null | undefined, options?: { alwaysShowYear?: boolean }): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const day = Number(m[3]);
  const month = MONTHS_GENITIVE[Number(m[2]) - 1] ?? '';
  const year = Number(m[1]);
  const currentYear = new Date().getFullYear();
  return year === currentYear && !options?.alwaysShowYear ? `${day} ${month}` : `${day} ${month} ${year}`;
}
